/**
 * GLSL ES 3.00 sources. Ports of vendor-ref/nanome/Assets/Resources/Display/{ImpostorCommon,ImpostorSphere,
 * ImpostorCylinder,ImpostorPasses}.hlsl and Assets/Resources/Skybox/{ProceduralSkyboxBake,ProceduralSkybox}.shader.
 * three.js prepends "#version 300 es" and the material defines (glslVersion = GLSL3), so no #version line here.
 *
 * Defines used across passes:
 *   FADE   applyFade = true (main, shell, mask, band passes); absent for binary shadow-style passes
 *   PICK   write the per-instance id instead of colour
 *   SHELL  far surface, unlit colour (highlight shell)              (ImpostorPasses.hlsl:11-19)
 *   MASK   near surface deflated to the real one, stencil only
 *   BAND   far surface pushed back by uDepthBand, stencil clear only
 *   CAPS   cylinders close ends not flagged open
 *   SHADOW shadow-caster pass: orthographic light rays, analytic silhouette only, depth from the rasterised proxy
 *          (IMPOSTOR_SHADOW_RASTER_DEPTH, ImpostorCommon.hlsl:19-25; ImpostorPasses.hlsl:106-116)
 */

/** View-space pixel scale, edge coverage, sRGB cubic and lighting (ImpostorCommon.hlsl:50-62, 131-151). */
export const IMPOSTOR_COMMON = /* glsl */ `
precision highp float;
precision highp int;
uniform mat4 projectionMatrix;
uniform vec2 uResolution;
#if defined(SHADOW)
uniform float uShadowMapSize;
#endif
// Pixels per view-space unit at the given view depth (ImpostorCommon.hlsl:50-55). abs(): render-to-texture flip.
// The shadow pass is orthographic: texels per unit, independent of depth (the clamp then floors at shadow texels).
float impostorPixelScale(float viewDepth) {
#if defined(SHADOW)
  return 0.5 * abs(projectionMatrix[1][1]) * uShadowMapSize;
#else
  float scale = 0.5 * abs(projectionMatrix[1][1]) * uResolution.y;
  return scale / max(viewDepth, 1e-3);
#endif
}
// View-space ray through the rasterised proxy point (ImpostorCommon.hlsl:34-46): from the eye, or straight down -z
// from the proxy point under the light's orthographic projection.
void impostorRay(vec3 positionVS, out vec3 rayOrigin, out vec3 rayDir) {
#if defined(SHADOW)
  rayOrigin = vec3(positionVS.xy, 0.0);
  rayDir = vec3(0.0, 0.0, -1.0);
#else
  rayOrigin = vec3(0.0);
  rayDir = normalize(positionVS);
#endif
}
// 1-pixel falloff of the view-space signed distance to the silhouette (ImpostorCommon.hlsl:59-62).
float impostorEdgeCoverage(float sd, float viewDepth) {
  return clamp(sd * impostorPixelScale(viewDepth) + 0.5, 0.0, 1.0);
}
// sRGB -> linear cubic approximation (ImpostorCommon.hlsl:131-134).
vec3 impostorGammaToLinear(vec3 sRGB) {
  return sRGB * (sRGB * (sRGB * 0.305306011 + 0.682171111) + 0.012522878);
}
// GL window depth from a view-space point: clip z/w remapped from [-1,1] (ImpostorCommon.hlsl:102-116).
float impostorClipDepth(vec3 positionVS) {
  vec4 c = projectionMatrix * vec4(positionVS, 1.0);
  return (c.z / c.w) * 0.5 + 0.5;
}
struct ImpostorHit { vec3 positionVS; vec3 normalVS; vec3 rayDirVS; float field; };
`;

/**
 * The light rig, fixed in view space, and the two lighting models prod uses on it (mirrored in lighting.ts):
 *  - impostorLighting: ImpostorCommon.hlsl:138-151, albedo * (LightingLambert + SampleSH) + LightingSpecular (Blinn-Phong);
 *  - litLighting: URP Lit for ribbons and surfaces (URP 17.3.0 Lighting.hlsl UniversalFragmentPBR ->
 *    LightingPhysicallyBased, BRDF.hlsl DirectBRDFSpecular, GlobalIllumination with occlusion 1) at metallic 0.
 * SampleSH is the flat ambient colour for every normal (Main.unity:23-27, ambient mode Flat). The environment
 * reflection of URP Lit is left out (docs/research/lighting-parity.md).
 */
export const LIGHTING = /* glsl */ `
uniform vec3 uLightDir;      // view space, unit, points toward the light
uniform vec3 uLightColor;    // linear, intensity folded in (white, 1)
uniform float uAmbient;      // flat ambient, linear
uniform float uSpecExp;      // impostors: exp2(10 * smoothness + 1)
uniform vec3 uSpecColor;     // impostors
uniform vec4 uBrdf;          // lit meshes: x roughness^2, y roughness^2 - 1, z roughness * 4 + 2, w 1 - reflectivity
uniform float uDielectricSpec; // lit meshes: specular colour F0 (kDielectricSpec, 0.04)
#if !defined(SHADOW) && !defined(PICK)
// Main-light shadow (GetMainLight(TransformWorldToShadowCoord(positionWS)).shadowAttenuation, ImpostorCommon.hlsl:140-141)
// with URP soft shadows quality Low (SampleShadowmapFilteredLowQuality): four hardware bilinear compare taps at
// +-0.5 texel, averaged; strength lerp; lit outside the map (BEYOND_SHADOW_FAR); distance fade over the cascade border.
uniform highp sampler2DShadow uShadowMap;
uniform mat4 uShadowMatrix;  // view space -> [0,1]^3 shadow coordinates
uniform vec4 uShadowParams;  // x enabled, y strength, z half texel in uv
uniform vec2 uShadowFade;    // fade = saturate(|p|^2 * x + y)
float impostorShadow(vec3 positionVS) {
  if (uShadowParams.x < 0.5) return 1.0;
  vec3 s = (uShadowMatrix * vec4(positionVS, 1.0)).xyz;
  if (s.z <= 0.0 || s.z >= 1.0 || any(lessThan(s.xy, vec2(0.0))) || any(greaterThan(s.xy, vec2(1.0)))) return 1.0;
  float o = uShadowParams.z;
  float a = textureLod(uShadowMap, vec3(s.xy + vec2(-o, -o), s.z), 0.0)
          + textureLod(uShadowMap, vec3(s.xy + vec2( o, -o), s.z), 0.0)
          + textureLod(uShadowMap, vec3(s.xy + vec2(-o,  o), s.z), 0.0)
          + textureLod(uShadowMap, vec3(s.xy + vec2( o,  o), s.z), 0.0);
  a = mix(1.0, a * 0.25, uShadowParams.y);
  float fade = clamp(dot(positionVS, positionVS) * uShadowFade.x + uShadowFade.y, 0.0, 1.0);
  return mix(a, 1.0, fade);
}
#else
float impostorShadow(vec3 positionVS) { return 1.0; }
#endif
vec3 impostorLighting(vec3 positionVS, vec3 n, vec3 v, vec3 albedo) {
  vec3 light = uLightColor * impostorShadow(positionVS);
  vec3 diffuse = light * max(dot(n, uLightDir), 0.0);
  vec3 h = normalize(uLightDir + v);
  vec3 specular = light * pow(max(dot(n, h), 0.0), uSpecExp) * uSpecColor;
  return albedo * (diffuse + vec3(uAmbient)) + specular;
}
// URP Lit, metallic 0: the diffuse colour is albedo * (1 - 0.04), the specular colour 0.04 times the GGX term of
// DirectBRDFSpecular, both scaled by radiance = light * shadow * N.L; the ambient probe lights the diffuse colour only.
vec3 litLighting(vec3 positionVS, vec3 n, vec3 v, vec3 albedo) {
  vec3 radiance = uLightColor * (impostorShadow(positionVS) * clamp(dot(n, uLightDir), 0.0, 1.0));
  vec3 h = normalize(uLightDir + v);
  float NoH = clamp(dot(n, h), 0.0, 1.0);
  float LoH = clamp(dot(uLightDir, h), 0.0, 1.0);
  float d = NoH * NoH * uBrdf.y + 1.00001;
  float specularTerm = uBrdf.x / ((d * d) * max(0.1, LoH * LoH) * uBrdf.z);
  vec3 diffuse = albedo * uBrdf.w;
  return (diffuse + vec3(uDielectricSpec * specularTerm)) * radiance + diffuse * uAmbient;
}
`;

/** Shared fragment epilogue for both impostor shapes (ImpostorPasses.hlsl:28-91). Expects `hit` and `surface`. */
const IMPOSTOR_FRAGMENT_TAIL = /* glsl */ `
  float alpha = impostorEdgeCoverage(hit.field, -hit.positionVS.z) * surface.a;
#if defined(BAND)
  float depthPush = uDepthBand;
#elif defined(MASK)
  float depthPush = 0.0;
#else
  // A partly covered pixel writes depth for the whole pixel; push it back so neighbours still draw (no sample mask).
  float depthPush = alpha < 0.999 ? uDepthBand : 0.0;
#endif
  gl_FragDepth = impostorClipDepth(hit.positionVS + hit.rayDirVS * depthPush);
  if (alpha < 0.003) discard;
#if defined(PICK)
  if (alpha < 0.5) discard;
  fragColor = vPick;
#elif defined(BAND) || defined(MASK)
  fragColor = vec4(0.0);
#elif defined(SHELL)
  fragColor = vec4(uShellColor, alpha);
#else
  if (surface.a < 0.999) {
    // Subpixel impostors are all grazing rim: flatten the normal toward the view (ImpostorPasses.hlsl:73-77).
    hit.normalVS = normalize(mix(hit.normalVS, -hit.rayDirVS, 1.0 - surface.a));
  }
  vec3 color = impostorLighting(hit.positionVS, hit.normalVS, -hit.rayDirVS, surface.rgb);
  fragColor = vec4(color, alpha);
#endif
`;

/** ImpostorSphere.hlsl:28-100 (vertex). Quad on the tangent plane at the sphere's nearest point. */
export const SPHERE_VERT = /* glsl */ `
${IMPOSTOR_COMMON}
in vec2 position;
in vec3 aCenter;
in float aRadius;
in vec3 aColor;
in vec4 aPick;
in float aRatio;
uniform mat4 modelViewMatrix;
uniform float uScale;
uniform float uMinPixelRadius;
out vec3 vPositionVS;
flat out vec4 vCenterRadiusVS;
flat out vec4 vColor;
flat out vec4 vPick;
void main() {
  vec3 centerVS = (modelViewMatrix * vec4(aCenter, 1.0)).xyz;
  float radius = aRadius * uScale;
#if defined(MASK)
  radius *= aRatio; // deflate to the real surface
#endif
  float pixelScale = impostorPixelScale(-centerVS.z);
  float trueRadius = radius;
  radius = max(radius, uMinPixelRadius / pixelScale);
  float coverage = 1.0;
  float edgePad = 0.0;
#if defined(FADE)
  float ratio = trueRadius / radius;
  coverage = ratio * ratio;
  edgePad = 1.0 / pixelScale;
#endif
#if defined(SHADOW)
  // Light-facing quad pushed to the far side of the sphere (quadDepth = -radius, ImpostorSphere.hlsl:64-80): the
  // raster depth sits behind the analytic surface, so a sphere never shadows its own lit side.
  vec3 positionVS = centerVS + vec3(position * (radius * 1.02 + edgePad), -radius);
#else
  float dist = max(length(centerVS), 1e-5);
  vec3 toCenter = centerVS / dist;
  vec3 upRef = abs(toCenter.y) < 0.95 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
  vec3 right = normalize(cross(upRef, toCenter));
  vec3 up = cross(toCenter, right);
  float planeDist = dist - radius;
  float halfSize = radius * sqrt(max(planeDist, 0.0) / (dist + radius)) * 1.02 + edgePad;
  vec3 positionVS = toCenter * planeDist + (right * position.x + up * position.y) * halfSize;
#endif
  vPositionVS = positionVS;
  vCenterRadiusVS = vec4(centerVS, radius);
  vColor = vec4(impostorGammaToLinear(aColor), coverage);
  vPick = aPick;
  gl_Position = projectionMatrix * vec4(positionVS, 1.0);
}
`;

/** ImpostorSphere.hlsl:118-141 + ImpostorPasses.hlsl forward fragment. */
export const SPHERE_FRAG = /* glsl */ `
${IMPOSTOR_COMMON}
${LIGHTING}
uniform float uDepthBand;
uniform vec3 uShellColor;
in vec3 vPositionVS;
flat in vec4 vCenterRadiusVS;
flat in vec4 vColor;
flat in vec4 vPick;
out vec4 fragColor;
ImpostorHit intersectSphereRoot(vec3 positionVS, vec4 centerRadius, float rootSign) {
  vec3 rayOrigin, rayDir;
  impostorRay(positionVS, rayOrigin, rayDir);
  vec3 oc = centerRadius.xyz - rayOrigin;
  float b = dot(rayDir, oc);
  float radius = centerRadius.w;
  float dPerp2 = dot(oc, oc) - b * b;
  float field = radius * radius - dPerp2;
  ImpostorHit hit;
  hit.field = field / max(2.0 * radius, 1e-6);
  hit.rayDirVS = rayDir;
  float t = b + rootSign * sqrt(max(field, 0.0));
  hit.positionVS = rayOrigin + rayDir * t;
  hit.normalVS = normalize(hit.positionVS - centerRadius.xyz);
  return hit;
}
void main() {
#if defined(SHADOW)
  // Analytic silhouette only; the far-side proxy supplies the depth (ImpostorPasses.hlsl:106-116).
  ImpostorHit hit = intersectSphereRoot(vPositionVS, vCenterRadiusVS, -1.0);
  if (hit.field < 0.0) discard;
  fragColor = vec4(0.0);
#else
#if defined(SHELL) || defined(BAND)
  const float rootSign = 1.0;
#else
  const float rootSign = -1.0;
#endif
  ImpostorHit hit = intersectSphereRoot(vPositionVS, vCenterRadiusVS, rootSign);
  vec4 surface = vColor;
${IMPOSTOR_FRAGMENT_TAIL}
#endif
}
`;

/** ImpostorCylinder.hlsl:34-104 (vertex). 8-vertex box: x,z in [-1,1], y in [0,1]. */
export const CYLINDER_VERT = /* glsl */ `
${IMPOSTOR_COMMON}
in vec3 position;
in vec3 aStart;
in vec3 aEnd;
in float aRadius;
in vec3 aColorStart;
in vec3 aColorEnd;
in float aFlags;
in vec4 aPick;
in float aRatio;
uniform mat4 modelViewMatrix;
uniform float uScale;
uniform float uMinPixelRadius;
out vec3 vPositionVS;
flat out vec4 vBaseRadiusVS;
flat out vec4 vAxisLenSqVS;
flat out vec4 vColorStart;
flat out vec4 vColorEnd;
flat out vec4 vPick;
void main() {
  vec3 baseVS = (modelViewMatrix * vec4(aStart, 1.0)).xyz;
  vec3 endVS = (modelViewMatrix * vec4(aEnd, 1.0)).xyz;
  vec3 axisVS = endVS - baseVS;
  float trueRadius = max(aRadius * uScale, 1e-6);
  // Any orthonormal pair perpendicular to the axis; (right, axis, forward) right-handed so the box winds outward.
  vec3 axisN = normalize(axisVS);
  vec3 helper = abs(axisN.x) < 0.9 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
  vec3 rightVS = normalize(cross(axisN, helper)) * trueRadius;
  vec3 forwardVS = cross(rightVS, axisN);
  int packed = int(aFlags + 0.5);
  bvec2 capped = bvec2((packed & 2) == 0, (packed & 4) == 0);
#if !defined(CAPS)
  capped = bvec2(false, false);
#endif
#if defined(MASK)
  // Deflate to the real surface; capped ends also sit one thickness past it (ImpostorCylinder.hlsl:61-69).
  float surface = aRatio;
  vec2 inset = vec2(capped.x ? 1.0 : 0.0, capped.y ? 1.0 : 0.0);
  vec3 axialInsetVS = axisN * ((1.0 - surface) * length(rightVS));
  baseVS += axialInsetVS * inset.x;
  axisVS -= axialInsetVS * (inset.x + inset.y);
  rightVS *= surface;
  forwardVS *= surface;
  trueRadius = max(length(rightVS), 1e-6);
#endif
  float radius = trueRadius;
  float baba = dot(axisVS, axisVS);
  vec3 midVS = baseVS + axisVS * 0.5;
  float pixelScale = impostorPixelScale(-midVS.z);
  radius = max(radius, uMinPixelRadius / pixelScale);
  float coverage = 1.0;
  float edgePad = 0.0;
#if defined(FADE)
  coverage = trueRadius / radius;
  edgePad = 1.0 / pixelScale;
#endif
  float radialScale = (radius * 1.02 + edgePad) / trueRadius;
  vec3 corner = position;
  float axialPad = (corner.y * 2.0 - 1.0) * edgePad * inversesqrt(max(baba, 1e-8));
  vec3 positionVS = baseVS + axisVS * (corner.y + axialPad) + rightVS * (corner.x * radialScale) + forwardVS * (corner.z * radialScale);
  vPositionVS = positionVS;
  vBaseRadiusVS = vec4(baseVS, radius);
  vAxisLenSqVS = vec4(axisVS, baba);
  vColorStart = vec4(impostorGammaToLinear(aColorStart), coverage);
  float bits = (capped.x ? 1.0 : 0.0) + (capped.y ? 2.0 : 0.0);
#if !defined(FADE)
  bits += 4.0;
#endif
  vColorEnd = vec4(impostorGammaToLinear(aColorEnd), bits);
  vPick = aPick;
  gl_Position = projectionMatrix * vec4(positionVS, 1.0);
}
`;

/** ImpostorCylinder.hlsl:120-266 (no sample mask path) + ImpostorPasses.hlsl forward fragment. */
export const CYLINDER_FRAG = /* glsl */ `
${IMPOSTOR_COMMON}
${LIGHTING}
uniform float uDepthBand;
uniform vec3 uShellColor;
in vec3 vPositionVS;
flat in vec4 vBaseRadiusVS;
flat in vec4 vAxisLenSqVS;
flat in vec4 vColorStart;
flat in vec4 vColorEnd;
flat in vec4 vPick;
out vec4 fragColor;
// Signed distance to the cap rim where the ray crosses the cap plane.
float cylinderCapField(vec3 oc, vec3 rayDir, vec3 capBase, float capT, float radius, float fieldScale) {
  vec3 capOffset = oc + rayDir * capT - capBase;
  return (radius * radius - dot(capOffset, capOffset)) * fieldScale;
}
ImpostorHit intersectCylinderRoot(vec3 positionVS, vec4 baseRadius, vec4 axisLenSq, float rootSign, bvec2 capped, bool overlap, out float axialY) {
  vec3 rayOrigin, rayDir;
  impostorRay(positionVS, rayOrigin, rayDir);
  float radius = baseRadius.w;
  vec3 ba = axisLenSq.xyz;
  float baba = axisLenSq.w;
  vec3 oc = rayOrigin - baseRadius.xyz;
  float bard = dot(ba, rayDir);
  float baoc = dot(ba, oc);
  float k2 = baba - bard * bard;
  float k1 = baba * dot(oc, rayDir) - baoc * bard;
  float k0 = baba * dot(oc, oc) - baoc * baoc - radius * radius * baba;
  float h = k1 * k1 - k2 * k0;
  float k2Safe = max(k2, baba * 1e-6);
  float fieldScale = 1.0 / max(2.0 * radius, 1e-6);
  ImpostorHit hit;
  hit.field = h / (baba * k2Safe) * fieldScale;
  hit.rayDirVS = rayDir;
  float sqrtH = sqrt(max(h, 0.0));
  float rs = inversesqrt(baba);
  float bardSafe = abs(bard) < 1e-7 ? 1e-7 : bard;
  float capT0 = -baoc / bardSafe;
  float capT1 = (baba - baoc) / bardSafe;
  // Without a sample mask an open end runs two pixels past the plane before it fades.
  float pixelScale = impostorPixelScale(-positionVS.z);
  float openSlack = overlap ? 2.0 / pixelScale : 0.0;
  float tNear = (-k1 - sqrtH) / k2Safe;
  float yNear = baoc + tNear * bard;
  float t = rootSign < 0.0 ? tNear : (-k1 + sqrtH) / k2Safe;
  float y = baoc + t * bard;
  bool onCap0 = y < 0.0 && capped.x;
  bool onCap1 = y > baba && capped.y;
  if (rootSign < 0.0) {
    if (onCap0) {
      hit.field = min(hit.field, cylinderCapField(oc, rayDir, vec3(0.0), capT0, radius, fieldScale));
    } else if (onCap1) {
      hit.field = min(hit.field, cylinderCapField(oc, rayDir, ba, capT1, radius, fieldScale));
    } else {
      if (!capped.x) hit.field = min(hit.field, y * rs + openSlack);
      if (!capped.y) hit.field = min(hit.field, (baba - y) * rs + openSlack);
    }
  } else {
    float startField = capped.x ? max(yNear, y) * rs : y * rs + openSlack;
    float endField = capped.y ? (baba - min(yNear, y)) * rs : (baba - y) * rs + openSlack;
    hit.field = min(hit.field, min(startField, endField));
    if (capped.x) hit.field = max(hit.field, cylinderCapField(oc, rayDir, vec3(0.0), capT0, radius, fieldScale));
    if (capped.y) hit.field = max(hit.field, cylinderCapField(oc, rayDir, ba, capT1, radius, fieldScale));
  }
  if (onCap0) {
    t = capT0; axialY = 0.0; hit.normalVS = -ba * rs;
  } else if (onCap1) {
    t = capT1; axialY = baba; hit.normalVS = ba * rs;
  } else {
    axialY = clamp(y, 0.0, baba);
    hit.normalVS = normalize(oc + rayDir * t - ba * (y / baba));
    // Past the end, draw only fully covered pixels and just behind, so the neighbour's wall wins depth.
    float overrun = max(max(-y, y - baba), 0.0) * rs;
    if (overlap && overrun > 0.0) {
      hit.field = hit.field * pixelScale < 0.5 ? -1.0 : hit.field;
      t += overrun;
    }
  }
  hit.positionVS = rayOrigin + rayDir * t;
  return hit;
}
void main() {
#if defined(SHADOW)
  const float rootSign = -1.0;
#elif defined(SHELL) || defined(BAND)
  const float rootSign = 1.0;
#else
  const float rootSign = -1.0;
#endif
  int bits = int(vColorEnd.a + 0.5);
  float axialY;
  ImpostorHit hit = intersectCylinderRoot(vPositionVS, vBaseRadiusVS, vAxisLenSqVS, rootSign, bvec2((bits & 1) != 0, (bits & 2) != 0), (bits & 4) == 0, axialY);
#if defined(SHADOW)
  // Silhouette only; the back faces of the box (Cull Front) supply depth behind the surface (CylinderImpostorWebGL.shader:139-145).
  if (hit.field < 0.0) discard;
  fragColor = vec4(0.0);
#else
  // Per-half bond colour, split at the axis midpoint (ImpostorCylinder.hlsl:262-266).
  vec3 albedo = axialY * 2.0 < vAxisLenSqVS.w ? vColorStart.rgb : vColorEnd.rgb;
  vec4 surface = vec4(albedo, vColorStart.a);
${IMPOSTOR_FRAGMENT_TAIL}
#endif
}
`;

/**
 * Lit vertex-colour mesh (ribbons, surfaces): URP Lit at metallic 0 (litLighting above) with the per-material
 * smoothness (0.15 ribbon, 0.3 surface) folded into uBrdf. Vertex colours are sRGB and linearised here, as the shader
 * graph does (Lit_VertexColor.shadergraph: Vertex Color -> Colorspace Conversion RGB to Linear -> Base Color).
 * Defines: PICK (per-vertex id), HULL (inverse hull: offset along the normal by uHullSize Å, only where aHighlight
 * > 0.5; drawn front-culled and unlit, RibbonRenderer.cs:35-72 / InverseHull.shadergraph _Size 0.1), UNLIT,
 * SHADOW (caster pass in light space with URP's ApplyShadowBias: away from the light by uShadowBias.x, inward along
 * the normal by uShadowBias.y * (1 - saturate(N.L)); the light looks down its -z, so N.L = n.z).
 */
export const MESH_VERT = /* glsl */ `
precision highp float;
in vec3 position;
in vec3 normal;
in vec3 color;
in vec4 aPick;
in float aHighlight;
in vec3 aHullDir;   // position-averaged normal: inflating along split face normals would tear the hull apart
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform mat3 normalMatrix;
uniform float uHullSize;
#if defined(SHADOW)
uniform vec2 uShadowBias;
#endif
out vec3 vPositionVS;
out vec3 vNormalVS;
out vec3 vColor;
out float vHighlight;
flat out vec4 vPick;
vec3 gammaToLinear(vec3 sRGB) { return sRGB * (sRGB * (sRGB * 0.305306011 + 0.682171111) + 0.012522878); }
void main() {
  vec3 p = position;
  vHighlight = aHighlight;
#if defined(HULL)
  p += normalize(aHullDir) * uHullSize;
#endif
  vec4 pVS = modelViewMatrix * vec4(p, 1.0);
#if defined(SHADOW)
  vec3 nLS = normalize(normalMatrix * normal);
  pVS.xyz += vec3(0.0, 0.0, -uShadowBias.x) - nLS * (uShadowBias.y * (1.0 - clamp(nLS.z, 0.0, 1.0)));
#endif
  vPositionVS = pVS.xyz;
  vNormalVS = normalMatrix * normal;
  vColor = gammaToLinear(color);
  vPick = aPick;
  gl_Position = projectionMatrix * pVS;
}
`;

export const MESH_FRAG = /* glsl */ `
precision highp float;
${LIGHTING}
uniform float uOpacity;
uniform vec3 uShellColor;
uniform float uPickDepthBias;
in vec3 vPositionVS;
in vec3 vNormalVS;
in vec3 vColor;
in float vHighlight;
flat in vec4 vPick;
out vec4 fragColor;
void main() {
#if defined(SHADOW)
  fragColor = vec4(0.0);
#elif defined(PICK)
  gl_FragDepth = gl_FragCoord.z + uPickDepthBias;
  fragColor = vPick;
#elif defined(HULL)
  // Only triangles whose three vertices belong to highlighted residues survive.
  if (vHighlight < 0.999) discard;
  fragColor = vec4(uShellColor, 1.0);
#elif defined(UNLIT)
  fragColor = vec4(vColor, uOpacity);
#else
  vec3 n = normalize(vNormalVS);
  if (!gl_FrontFacing) n = -n;
  vec3 v = normalize(-vPositionVS);
  fragColor = vec4(litLighting(vPositionVS, n, v, vColor), uOpacity);
#endif
}
`;

/** Fullscreen triangle vertex shader (positions in NDC, no attributes beyond `position`). */
export const FULLSCREEN_VERT = /* glsl */ `
precision highp float;
in vec2 position;
out vec2 vUv;
void main() {
  vUv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

/**
 * ProceduralSkyboxBake.shader (lines 63-205), verbatim port. Palette uniforms are raw sRGB floats with the tonemap
 * pre-inverted (SkyboxBaker.cs:72-82); the output is linear (GammaToLinearSpace at the end).
 */
export const SKYBOX_BAKE_FRAG = /* glsl */ `
precision highp float;
precision highp int;
in vec2 vUv;
uniform vec4 uZenithColor;
uniform vec4 uMidColor;
uniform vec4 uHorizonColor;
uniform vec4 uSunColor;
uniform vec4 uGroundNearColor;
uniform vec4 uGroundFarColor;
uniform float uFog;
uniform float uRidges;
uniform float uSpikiness;
uniform float uRoughness;
uniform float uHeight;
uniform float uFrequency;
uniform float uDepthSpread;
uniform vec4 uSunDir;
uniform float uSunWarmth;
uniform float uSeed;
out vec4 fragColor;
const float TWO_PI = 6.28318530717958647692;
const float HALF_PI = 1.57079632679489661923;
// PCG hash: bit-exact on every GPU (ProceduralSkyboxBake.shader:84-90).
float hash(uint n) {
  n = n * 747796405u + 2891336453u;
  n = ((n >> ((n >> 28u) + 4u)) ^ n) * 277803737u;
  n = (n >> 22u) ^ n;
  return float(n) * (1.0 / 4294967296.0);
}
// Periodic 1D value noise (:92-101).
float pnoise(float x, float period, uint seed) {
  float i = floor(x);
  float f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  uint i0 = uint(mod(i, period));
  uint i1 = uint(mod(i + 1.0, period));
  return mix(hash(seed * 668265263u + i0), hash(seed * 668265263u + i1), f);
}
// FBM with an integer base period, seamless around the circle (:103-127).
float ridge(float x, uint seed, float period) {
  float a = 0.0;
  float amp = 0.5;
  float norm = 0.0;
  for (int i = 0; i < 8; i++) {
    if (period > 2048.0) break;
    float n = pnoise(x * period, period, seed * 97u + uint(i) * 13u);
    float r = 1.0 - abs(n * 2.0 - 1.0);
    r = mix(r, r * r, uSpikiness);
    n = mix(n, r, uSpikiness);
    a += n * amp;
    norm += amp;
    period *= 2.0;
    amp *= uRoughness;
  }
  return a / norm;
}
float profile(float x, float fi) {
  uint seed = uint(fi) + 1u + uint(uSeed) * 7919u;
  float rank = uRidges - 1.0 - fi;
  float period = max(floor((6.0 + rank * 2.0) * uFrequency), 2.0);
  float r = ridge(x, seed, period);
  float peaks = 1.0 - abs(pnoise(x * 3.0, 3.0, seed * 31u) * 2.0 - 1.0);
  r += pow(max(smoothstep(0.4, 1.0, peaks), 1e-4), 1.0 + uSpikiness * 3.0) * (0.3 + uSpikiness * 0.5);
  return r;
}
vec3 gammaToLinear(vec3 sRGB) { return sRGB * (sRGB * (sRGB * 0.305306011 + 0.682171111) + 0.012522878); }
void main() {
  // x = azimuth; y = warped elevation: sqrt warp packs rows near the horizon (:143-147).
  float az = (vUv.x - 0.5) * TWO_PI;
  float s = vUv.y * 2.0 - 1.0;
  float el = HALF_PI * s * abs(s);
  vec3 rd = vec3(cos(el) * sin(az), sin(el), cos(el) * cos(az));
  float elev = rd.y;
  float x = vUv.x;
  vec3 sunDir = normalize(uSunDir.xyz);
  float sd = max(dot(rd, sunDir), 1e-4);
  float t = clamp(elev, 0.0, 1.0);
  vec3 sky = mix(uHorizonColor.rgb, uMidColor.rgb, smoothstep(0.0, 0.25, t));
  sky = mix(sky, uZenithColor.rgb, smoothstep(0.2, 0.9, t));
  sky += uSunColor.rgb * pow(sd, 6.0) * 0.7 * uSunWarmth;
  sky += uSunColor.rgb * pow(sd, 64.0) * 0.8;
  sky += uSunColor.rgb * smoothstep(0.9995, 0.9999, sd);
  sky = mix(sky, uHorizonColor.rgb, exp(-abs(elev) * 18.0) * 0.5);
  vec3 fogCol = mix(uHorizonColor.rgb, uSunColor.rgb, pow(sd, 3.0) * uSunWarmth);
  vec3 col = sky;
  float aa = max(fwidth(elev) * 1.5, 1e-5);
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    if (fi >= uRidges) break;
    float depth = 1.0 - fi / max(uRidges - 1.0, 1.0);
    float r = profile(x, fi);
    float baseline = -0.02 - fi * 0.022 * uDepthSpread;
    float height = (0.10 + fi * 0.02 * uDepthSpread) * uHeight;
    float h = baseline + r * height;
    float m = 1.0 - smoothstep(h - aa, h + aa, elev);
    vec3 rock = mix(uGroundNearColor.rgb, uGroundFarColor.rgb, depth * 0.6);
    float baseHaze = smoothstep(0.0, 0.12, h - elev) * 0.25 * uFog;
    float fogAmt = 1.0 - exp(-depth * depth * (uFog * 6.0 + 0.4));
    vec3 layerCol = mix(rock, fogCol, min(fogAmt + baseHaze, 1.0));
    col = mix(col, layerCol, m);
  }
  col = mix(col, fogCol, smoothstep(0.0, -0.25, elev) * uFog * 0.6);
  col = col / (col + 0.6) * 1.55;
  col = pow(max(col, vec3(1e-4)), vec3(0.95));
  float dither = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
  col = clamp(col + (dither - 0.5) / 255.0, 0.0, 1.0);
  col = gammaToLinear(col);
  fragColor = vec4(col, 1.0);
}
`;

/**
 * ProceduralSkybox.shader: sample the equirect by view direction. The direction comes from the inverse projection
 * (the camera never rotates, so the backdrop is fixed on screen). Unity d = (x, y, -z) of the GL view direction.
 */
export const SKYBOX_DRAW_FRAG = /* glsl */ `
precision highp float;
in vec2 vUv;
uniform sampler2D uSky;
uniform mat4 uInvProjection;
out vec4 fragColor;
const float TWO_PI = 6.28318530717958647692;
const float HALF_PI = 1.57079632679489661923;
void main() {
  vec4 p = uInvProjection * vec4(vUv * 2.0 - 1.0, 1.0, 1.0);
  vec3 dGl = normalize(p.xyz / p.w);
  vec3 d = vec3(dGl.x, dGl.y, -dGl.z);
  float u = atan(d.x, d.z) / TWO_PI + 0.5;
  float t = asin(clamp(d.y, -1.0, 1.0)) / HALF_PI;
  float v = sign(t) * sqrt(abs(t)) * 0.5 + 0.5;
  float uWrap = fract(u + 0.5);
  vec2 g1 = vec2(dFdx(u), dFdy(u));
  vec2 g2 = vec2(dFdx(uWrap), dFdy(uWrap));
  vec2 gu = dot(g1, g1) < dot(g2, g2) ? g1 : g2;
  vec2 gv = vec2(dFdx(v), dFdy(v));
  fragColor = vec4(textureGrad(uSky, vec2(u, v), vec2(gu.x, gv.x), vec2(gu.y, gv.y)).rgb, 1.0);
}
`;

/** Resolve blit: linear scene texture -> sRGB canvas (exact piecewise encode). */
export const COMPOSITE_FRAG = /* glsl */ `
precision highp float;
in vec2 vUv;
uniform sampler2D uScene;
out vec4 fragColor;
vec3 linearToSrgb(vec3 c) {
  vec3 lo = c * 12.92;
  vec3 hi = 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055;
  return mix(lo, hi, step(vec3(0.0031308), c));
}
void main() {
  vec4 s = texture(uScene, vUv);
  fragColor = vec4(linearToSrgb(clamp(s.rgb, 0.0, 1.0)), s.a);
}
`;

/** Residue label billboards: instanced quads over a canvas text atlas, sized in Å, white by default. */
export const LABEL_VERT = /* glsl */ `
precision highp float;
in vec2 position;
in vec3 aCenter;
in vec4 aUvRect;   // u0 v0 u1 v1
in vec2 aSize;     // width, height in Å
in vec3 aColor;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform float uScale;
out vec2 vUv;
out vec3 vColor;
void main() {
  vec3 centerVS = (modelViewMatrix * vec4(aCenter, 1.0)).xyz;
  vec3 pVS = centerVS + vec3(position * aSize * 0.5 * uScale, 0.0);
  vUv = mix(aUvRect.xy, aUvRect.zw, position * 0.5 + 0.5);
  vColor = aColor;
  gl_Position = projectionMatrix * vec4(pVS, 1.0);
}
`;

export const LABEL_FRAG = /* glsl */ `
precision highp float;
in vec2 vUv;
in vec3 vColor;
uniform sampler2D uAtlas;
out vec4 fragColor;
vec3 gammaToLinear(vec3 sRGB) { return sRGB * (sRGB * (sRGB * 0.305306011 + 0.682171111) + 0.012522878); }
void main() {
  float a = texture(uAtlas, vUv).a;
  if (a < 0.01) discard;
  fragColor = vec4(gammaToLinear(vColor), a);
}
`;
